import { Facebook, ChevronDown, ExternalLink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { BRAND } from "@/lib/brand";

const FB_LINKS = [
  {
    id: "page",
    label: "Public Page",
    sub: "Crown Jewels MC Houston",
    href: BRAND.social.facebook,
  },
  {
    id: "mc-group",
    label: "MC Members Group",
    sub: "Motorcycle Club · Private",
    href: BRAND.social.facebookMcGroup,
  },
  {
    id: "sc-group",
    label: "SC Members Group",
    sub: "Social Club · Private",
    href: BRAND.social.facebookScGroup,
  },
];

/** Facebook icon that opens a dropdown menu of all our FB destinations. */
export function FacebookIconMenu({
  testIdPrefix = "facebook-menu",
  className = "",
  align = "start",
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={`relative inline-flex items-center justify-center gap-1 ${className}`}
        data-testid={`${testIdPrefix}-trigger`}
        aria-label="Facebook options"
      >
        <Facebook className="w-4 h-4" />
        <ChevronDown className="w-3 h-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="bg-[#0a0a0a] border border-white/10 rounded-none min-w-[260px] p-0"
        data-testid={`${testIdPrefix}-content`}
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.3em] text-brand px-4 pt-4 pb-2">
          Find us on Facebook
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {FB_LINKS.map((l) => (
          <DropdownMenuItem
            asChild
            key={l.id}
            className="text-white/80 hover:text-brand focus:text-brand focus:bg-white/5 rounded-none cursor-pointer px-4 py-3"
          >
            <a
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-3"
              data-testid={`${testIdPrefix}-${l.id}`}
            >
              <Facebook className="w-4 h-4 text-brand mt-0.5 shrink-0" />
              <span className="flex-1">
                <span className="block text-sm text-white">{l.label}</span>
                <span className="block text-[11px] text-white/50 mt-0.5">
                  {l.sub}
                </span>
              </span>
              <ExternalLink className="w-3.5 h-3.5 text-white/40 mt-1 shrink-0" />
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Full-width community CTA block — for use inside Contact section. */
export function FacebookCommunityBlock() {
  return (
    <div
      className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-8"
      data-testid="facebook-community-block"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="inline-flex items-center justify-center w-10 h-10 border border-brand/50">
          <Facebook className="w-4 h-4 text-brand" />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand">
            On Facebook
          </div>
          <h4 className="font-serif text-xl text-white leading-tight">
            Join our community
          </h4>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {FB_LINKS.map((l) => (
          <a
            key={l.id}
            href={l.href}
            target="_blank"
            rel="noreferrer"
            className="group relative p-4 bg-[#050505] border border-white/10 hover:border-brand/60 transition-all duration-500"
            data-testid={`fb-community-${l.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <Facebook className="w-4 h-4 text-brand" />
              <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-brand transition-colors" />
            </div>
            <div className="text-sm font-serif text-white group-hover:text-brand transition-colors">
              {l.label}
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-1">
              {l.sub}
            </div>
            <span className="absolute bottom-0 left-0 h-px w-0 bg-brand group-hover:w-full transition-all duration-700" />
          </a>
        ))}
      </div>
    </div>
  );
}
